import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Card, message } from 'antd';
import { fetchRegister } from '@/apis/user';
import { requestRegistrationCode } from '@/apis/schools';
import { getRequestErrorMessage } from '@/utils/requestError';
import SchoolSelect from '@/components/schoolselect';
import StandaloneBack from '@/components/standaloneback';
import { useNavigate, Link } from 'react-router-dom';
import logo from '@/assets/logo.png';
import './index.scss';

const normalizeEmail = value => (value || '').trim().toLowerCase();
const Register = () => {
  const navigate = useNavigate(); const [form] = Form.useForm();
  const [challenge, setChallenge] = useState(null); const [cooldown, setCooldown] = useState(null);
  const [sending, setSending] = useState(false); const [submitting, setSubmitting] = useState(false); const [now, setNow] = useState(Date.now());
  const email = Form.useWatch('email', form); const alive = useRef(false); const sendingLock = useRef(false); const submitLock = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { if (!cooldown) return; const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, [cooldown]);
  const remaining = cooldown?.email === normalizeEmail(email) ? Math.max(0, Math.ceil((cooldown.until - now) / 1000)) : 0;
  const sendCode = async () => {
    if (sendingLock.current || submitLock.current || remaining) return;
    sendingLock.current = true; setSending(true);
    let mailbox;
    try {
      const values = await form.validateFields(['email']); mailbox = normalizeEmail(values.email);
      const result = await requestRegistrationCode(mailbox);
      if (!alive.current || normalizeEmail(form.getFieldValue('email')) !== mailbox) return;
      if (!result.verification_request_id) throw new Error('验证码请求响应无效，请重新获取');
      const time = Date.now(); setNow(time);
      setChallenge({ email: mailbox, id: result.verification_request_id, expiresAt: time + (result.expires_in || 600) * 1000 });
      setCooldown({ email: mailbox, until: time + (result.resend_after || 60) * 1000 });
      form.setFieldValue('verification_code', '');
      message.success(result.detail || '若该邮箱可用于注册，验证码邮件已发送，请检查收件箱。');
    } catch (error) {
      if (!alive.current || error.errorFields) return;
      if (mailbox && normalizeEmail(form.getFieldValue('email')) === mailbox) {
        const wait = Number(error.response?.headers?.['retry-after']); const time = Date.now(); setNow(time);
        setCooldown({ email: mailbox, until: time + (Number.isFinite(wait) && wait > 0 ? wait : 60) * 1000 });
        setChallenge(null);
      }
      message.error(getRequestErrorMessage(error, '验证码获取失败，请稍后重试'));
    } finally { sendingLock.current = false; if (alive.current) setSending(false); }
  };
  const onFinish = async values => {
    if (submitLock.current || sendingLock.current) return;
    const mailbox = normalizeEmail(values.email);
    if (!challenge || challenge.email !== mailbox || challenge.expiresAt <= Date.now()) { message.warning('请先获取当前邮箱的有效验证码'); return; }
    submitLock.current = true; setSubmitting(true);
    try {
      const result = await fetchRegister({ ...values, username: values.username.trim(), email: mailbox, school: values.school, verification_request_id: challenge.id });
      if (!alive.current) return;
      if (result.success) { message.success('注册成功！请登录'); navigate('/login', { replace: true }); }
      else message.error(result.error);
    } finally { submitLock.current = false; if (alive.current) setSubmitting(false); }
  };
  return <div className="register standalonePage">
    <StandaloneBack disabled={submitting || sending} />
    <div className="demo-logo"><Link to="/" aria-label="返回首页"><img src={logo} alt="IPartner" height={64} /></Link></div>
    <Card title="注册账号" variant="borderless">
      <Form form={form} name="register" layout="vertical" onFinish={onFinish} scrollToFirstError onValuesChange={changed => {
        if ('email' in changed) { setChallenge(null); form.setFieldValue('verification_code', ''); }
      }}>
        <Form.Item name="username" label="用户名" rules={[{ required: true, whitespace: true, message: '请输入用户名' }]}><Input maxLength={12} autoComplete="username" disabled={submitting} /></Form.Item>
        <Form.Item name="nickname" label="昵称（可选）"><Input maxLength={12} disabled={submitting} /></Form.Item>
        <Form.Item name="school" label="学校" rules={[{ required: true, message: '请选择学校' }]}><SchoolSelect disabled={submitting} /></Form.Item>
        <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }, { min: 8, max: 256, message: '密码长度为 8–256 个字符，请避免常见密码' }]}><Input.Password maxLength={256} autoComplete="new-password" disabled={submitting} /></Form.Item>
        <Form.Item name="password2" label="确认密码" dependencies={['password']} rules={[{ required: true, message: '请确认密码' }, ({ getFieldValue }) => ({ validator: (_, value) => !value || getFieldValue('password') === value ? Promise.resolve() : Promise.reject(new Error('两次密码不一致')) })]}><Input.Password maxLength={256} autoComplete="new-password" disabled={submitting} /></Form.Item>
        <Form.Item name="email" label="邮箱" rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '请输入正确的邮箱格式' }]}><Input maxLength={254} autoComplete="email" disabled={submitting} /></Form.Item>
        <Form.Item className="register-code-field" label="邮箱验证码" required extra="验证码为 6 位数字，10 分钟内有效；重发后旧验证码失效。">
          <div className="register-code-row"><Form.Item name="verification_code" noStyle rules={[{ required: true, message: '请输入邮箱验证码' }, { pattern: /^[0-9]{6}$/, message: '请输入 6 位数字验证码' }]}>
            <Input aria-label="邮箱验证码" maxLength={6} inputMode="numeric" autoComplete="one-time-code" placeholder="6 位数字" disabled={submitting} />
          </Form.Item><Button onClick={sendCode} loading={sending} disabled={submitting || sending || remaining > 0}>{remaining ? `${remaining} 秒后重发` : '获取验证码'}</Button></div>
        </Form.Item>
        <Form.Item className="register-submit"><Button block type="primary" htmlType="submit" loading={submitting} disabled={sending}>注册账号</Button></Form.Item>
      </Form>
      <p className="register-login">已有账号？<Link to="/login">去登录</Link></p>
    </Card>
  </div>;
};
export default Register;
